import { useContext } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { School, Globe, Layers, BookOpen, Users, AlertTriangle, LayoutDashboard, UserCog, Menu, X, Sparkles } from 'lucide-react';
import { AuthContext } from '@/lib/auth';
import { LogoutButton } from '@/components/auth/LogoutButton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';

export default function Layout() {
  const location = useLocation();
  const { session } = useContext(AuthContext);
  const user = session?.user;

  const navigation = [
    { 
      name: 'Dashboard', 
      href: '/admin', 
      icon: LayoutDashboard,
      color: 'text-violet-500'
    },
    { 
      name: 'Reported Issues', 
      href: '/admin/reported-issues', 
      icon: AlertTriangle,
      color: 'text-orange-500'
    },
    {
      name: 'AI Summaries',
      href: '/admin/ai-summaries',
      icon: Sparkles,
      color: 'text-purple-500'
    },
    { 
      name: 'Schools', 
      href: '/admin/schools', 
      icon: School,
      color: 'text-blue-500'
    },
    { 
      name: 'Regions', 
      href: '/admin/regions', 
      icon: Globe,
      color: 'text-emerald-500'
    },
    { 
      name: 'School Levels', 
      href: '/admin/school-levels', 
      icon: Layers,
      color: 'text-amber-500'
    },
    { 
      name: 'Grades', 
      href: '/admin/grades', 
      icon: BookOpen,
      color: 'text-rose-500'
    },
    { 
      name: 'Recipients', 
      href: '/admin/recipients', 
      icon: Users,
      color: 'text-purple-500'
    },
  ];
  
  // Add Users page for managers only
  if (user?.role === 'app.manager') {
    navigation.push({
      name: 'Users',
      href: '/admin/users',
      icon: UserCog,
      color: 'text-indigo-500'
    });
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="flex h-screen">
        {/* Sidebar */}
        <div className="hidden md:flex w-64 flex-col fixed inset-y-0">
          <div className="flex flex-col flex-grow bg-card border-r border-border">
            <div className="flex h-16 items-center gap-2 px-4 border-b border-border">
              <School className="h-6 w-6 text-blue-500" />
              <span className="font-semibold flex-1">Teacher Reporter</span>
              {session?.user && (
                <div className="flex items-center gap-3">
                  <span className="text-sm text-muted-foreground">{session.user.name}</span>
                  <Avatar className="h-8 w-8">
                    <AvatarFallback>{session.user.name?.[0]?.toUpperCase()}</AvatarFallback>
                  </Avatar>
                </div>
              )}
            </div>
            <nav className="flex-1 space-y-1 px-2 py-4 overflow-y-auto">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`
                      flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors
                      ${isActive 
                        ? 'bg-primary/10 text-primary' 
                        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'}
                    `}
                  >
                    <item.icon className={`h-5 w-5 ${item.color}`} />
                    {item.name}
                  </Link>
                );
              })}
            </nav>
            <div className="p-2 border-t">
              <LogoutButton />
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        <div className="md:hidden fixed inset-x-0 top-0 z-50 bg-card border-b border-border">
          <div className="flex h-16 items-center justify-between px-4">
            <div className="flex items-center gap-3">
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="shrink-0">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="p-0">
                  <div className="flex flex-col h-full">
                    <div className="flex h-16 items-center gap-2 px-4 border-b border-border">
                      <School className="h-6 w-6 text-blue-500" />
                      <span className="font-semibold flex-1">Teacher Reporter</span>
                    </div>
                    <nav className="flex-1 space-y-1 px-2 py-4 overflow-y-auto">
                      {navigation.map((item) => {
                        const isActive = location.pathname === item.href;
                        return (
                          <Link
                            key={item.name}
                            to={item.href}
                            className={`
                              flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors
                              ${isActive 
                                ? 'bg-primary/10 text-primary' 
                                : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'}
                            `}
                          >
                            <item.icon className={`h-5 w-5 ${item.color}`} />
                            {item.name}
                          </Link>
                        );
                      })}
                    </nav>
                    <div className="p-2 border-t">
                      <LogoutButton />
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
              <School className="h-6 w-6 text-blue-500" />
              <span className="font-semibold">Teacher Reporter</span>
            </div>
            {session?.user && (
              <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground">{session.user.name}</span>
                <Avatar className="h-8 w-8">
                  <AvatarFallback>{session.user.name?.[0]?.toUpperCase()}</AvatarFallback>
                </Avatar>
              </div>
            )}
          </div>
        </div>

        {/* Main content */}
        <main className="flex-1 md:ml-64 p-4 pt-20 md:pt-4">
          <div className="mx-auto max-w-7xl">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}